import { useState } from 'react';
import { Fragment } from 'react/jsx-runtime';
import { useTranslation } from 'react-i18next';
import { getRepubMetaFromRepub } from '@cx-web/utils';
import { type DehydratedState } from '@tanstack/react-query';

import Filters from './Filters';
import { GridGroupOffers } from './GridGroupOffers';
import { useMarkets, useTabs } from './hooks';
import { invalidDehydratedState } from './invalidDehydratedState';
import { OddsGrid } from './OddsGrid';
import Tabs, { type TabName } from './Tabs';
import { type GridGroupProps } from './types';

// Shared GridGroup component (NGF-6507), single source of truth for RR7 atlas and atlas-astro. An
// app-owned tabbed odds interface: interactive Best Odds / Offers (/ optional Stats) tabs, market-group
// filter buttons, and a per-market <OddsGrid> with embedded <GridGroupOffers>. Request-context-agnostic:
// `resourcesUrl` and `onError` are props each app fills (RR7 from useUIConfig()/captureException,
// atlas-astro from genericConfig.resourcesUrl / a console shim). Odds clicks add to the betslip via the
// BetslipShell window globals (NGF-6294) — no betslip context here.
export const GridGroup = ({
  clickoutUrl,
  componentArgs: {
    DISABLE_GRID_TAB_STATS,
    DISABLE_OFFER_WIDGETS,
    bookmakers,
    centrifugoUrl,
    enableLivePrices,
    gridOfferData,
    gridOffers,
    marketGroups,
    oddsGrids,
    oddsShown,
    overrideBookies,
    overrideMarketIds,
    projectionsUrl,
    selectedMarketId: initialSelectedMarketId,
    tabbedOffers,
    tabbedOffersData,
  },
  currentPathWithRepub,
  deviceType,
  geoLocationInfo,
  gtmId,
  oddsFormat,
  onError,
  repub,
  requestUrl,
  resourcesUrl,
}: GridGroupProps) => {
  const isOC = getRepubMetaFromRepub(repub).repubIs.OC;

  const { t } = useTranslation('label');
  const OFFERS = t('CX_OFFERS') as TabName;
  const BEST_ODDS = t('CX_BEST_ODDS') as TabName;

  const { handleTabChange, selectedTab, tabElements } = useTabs({
    DISABLE_GRID_TAB_STATS,
    projectionsUrl,
    repub,
  });

  const [selectedMarketId, setSelectedMarketId] = useState<number | null>(initialSelectedMarketId ?? null);

  const { handleMarketChange, marketGroupNames, selectedMarketGroup } = useMarkets(
    marketGroups,
    selectedMarketId,
    setSelectedMarketId,
  );

  // NGF-6268: GridGroupOffers must inject the offers-clickout props (the modules no longer read them
  // from provider context). These request-scoped values come straight from props (gtmId is merged in by
  // each app's render path from the loader response).
  const offersClickoutConfig = {
    clickoutUrl,
    currentPathWithRepub,
    deviceType,
    fullPageUrl: requestUrl,
    gtmId,
  };

  const renderGridOffers = () => {
    return gridOffers ? (
      <GridGroupOffers
        {...gridOffers}
        {...offersClickoutConfig}
        className="grid"
        dehydratedState={gridOfferData}
        onError={onError}
      />
    ) : null;
  };

  const renderSubeventMarkets = () => {
    if (!oddsGrids || !marketGroups[selectedMarketGroup]) return null;

    const activeSubeventMarkets = marketGroups[selectedMarketGroup];

    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        {activeSubeventMarkets
          .filter(market => oddsGrids[market.ocMarketId])
          .map((market, idx) => {
            const dehydratedState = oddsGrids[market.ocMarketId];
            if (invalidDehydratedState(dehydratedState)) return null;
            return (
              <Fragment key={`${market.marketDisplayName}-${selectedMarketGroup}`}>
                <OddsGrid
                  bestOddsStartExpanded={selectedMarketId ? selectedMarketId === market.ocMarketId : true}
                  // @ts-ignore
                  bookmakersConfig={bookmakers}
                  centrifugoUrl={centrifugoUrl}
                  countryCode={geoLocationInfo.countryCode}
                  dataApiEndpoint="/api"
                  dehydratedState={oddsGrids[market.ocMarketId] as DehydratedState}
                  deviceType={deviceType}
                  enableLivePrices={enableLivePrices}
                  gridStartsExpanded={deviceType === 'MOBILE'}
                  marketId={market.ocMarketId}
                  marketIndex={idx}
                  marketName={market.marketDisplayName}
                  oddsFormat={oddsFormat}
                  // @ts-ignore
                  oddsShown={oddsShown}
                  onError={onError}
                  overrideBookies={overrideBookies}
                  overrideMarketIds={overrideMarketIds}
                  repub={repub}
                  resourcesUrl={resourcesUrl}
                  subdivision={geoLocationInfo.subdivisionCode}
                  subeventType={market.subeventType}
                  tooltipForNewUser={idx === 0}
                />
                {idx === 0 ? renderGridOffers() : null}
              </Fragment>
            );
          })}
      </div>
    );
  };

  const renderActiveTabContent = () => {
    if (selectedTab.name === BEST_ODDS) {
      return (
        <>
          <Filters
            dataTestId="best-odds-tab-filters"
            handleMarketChange={handleMarketChange}
            isOC={isOC}
            marketGroupNames={marketGroupNames}
            selectedMarketGroup={selectedMarketGroup}
          />

          <div
            data-testid="best-odds-tab-subevent-markets"
            style={{
              width: '100%',
              margin: 'auto',
              maxWidth: 'var(--ogm-max-width)',
              padding: '0 16px 20px 16px',
            }}
          >
            {renderSubeventMarkets()}
          </div>
        </>
      );
    }

    if (selectedTab.name === OFFERS) {
      return tabbedOffers ? (
        <div
          data-testid="offers-tab-div"
          style={{
            width: '100%',
            padding: '0 16px',
          }}
        >
          <GridGroupOffers
            {...tabbedOffers}
            {...offersClickoutConfig}
            DISABLE_OFFER_WIDGETS={DISABLE_OFFER_WIDGETS}
            className="offersTab"
            dehydratedState={tabbedOffersData}
            onError={onError}
          />
        </div>
      ) : null;
    }
  };

  return (
    <>
      <Tabs handleTabChange={handleTabChange} isOC={isOC} selectedTab={selectedTab} tabElements={tabElements} />
      {renderActiveTabContent()}
    </>
  );
};
